import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import styles from "./massengermodal.module.css";
import { getMessengerProfileTypeKey } from "../../../utils/messengerProfiles";

const MessengerProfileTabs = ({
  activeChatsUnreadCount,
  handleSelect,
  isError,
  isLoading,
  isMarketerActive,
  isUserActive,
  forceGeneralPublicActive = false,
  onSelectUserProfile,
  onSelectShopProfile,
  onSelectMarketerProfile,
  onRetry,
  connectedProfiles = [],
  connectedProfilesLoading = false,
  connectedProfilesError = "",
  onOpenConnectedProfile,
  selectedConnectedTabId = "",
  pageMode,
  focusProfile,
  profiles,
  profileTabs,
  selectedProfile,
}) => {
  const trackRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const isMouseDownRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const isDraggingRef = useRef(false);

  const userProfile = profiles.find((profile) => profile.isGeneralPublic);
  const marketerProfile = profiles.find((profile) => profile.isMarketer);
  const showProfileSwitcherTabs = !focusProfile;

  const userCategoryTab = connectedProfiles.find((tab) => {
    const tId = String(tab.connectedProfileTabId || tab.id || "")
      .replace(/^type:/, "")
      .toLowerCase()
      .replace(/[\s_-]+/g, "");
    return tId === "user" || tId === "generalpublic";
  });
  const userCategoryUnread = Number(userCategoryTab?.unreadCount) || 0;

  const userUnreadCount = Math.max(
    (Number(userProfile?.unreadCount) || 0) + userCategoryUnread,
    isUserActive ? activeChatsUnreadCount : 0,
  );

  const isUserActiveTab = Boolean(
    (isUserActive || forceGeneralPublicActive) &&
    (!selectedConnectedTabId ||
      selectedConnectedTabId === "type:user" ||
      selectedConnectedTabId === "type:generalpublic")
  );

  const isMarketerActiveTab = Boolean(
    !isUserActiveTab &&
    (isMarketerActive ||
      selectedConnectedTabId === "type:marketerShop" ||
      selectedConnectedTabId.replace(/^type:/, "").toLowerCase() === "marketershop")
  );

  const isMatchingCategory = (a, b) => {
    if (!a || !b) return false;
    const normA = String(a).replace(/^type:/, "").toLowerCase().replace(/[\s_-]+/g, "");
    const normB = String(b).replace(/^type:/, "").toLowerCase().replace(/[\s_-]+/g, "");
    if (normA === normB) return true;
    if (normA.includes("whole") && normB.includes("whole")) return true;
    if (normA.includes("retail") && normB.includes("retail")) return true;
    if (
      (normA.includes("food") || normA.includes("restaurant")) &&
      (normB.includes("food") || normB.includes("restaurant"))
    )
      return true;
    if (normA.includes("service") && normB.includes("service")) return true;
    if (normA.includes("manufacturer") && normB.includes("manufacturer")) return true;
    if (normA.includes("rawmaterial") && normB.includes("rawmaterial")) return true;
    if (normA.includes("gem") && normB.includes("gem")) return true;
    if (
      (normA.includes("vehicle") || normA.includes("usedvehicle")) &&
      (normB.includes("vehicle") || normB.includes("usedvehicle"))
    )
      return true;
    if (normA.includes("used") && normB.includes("used")) return true;
    if (normA.includes("farm") && normB.includes("farm")) return true;
    if (normA.includes("recycle") && normB.includes("recycle")) return true;
    if (normA.includes("transport") && normB.includes("transport")) return true;
    if (normA.includes("property") && normB.includes("property")) return true;
    if (normA.includes("job") && normB.includes("job")) return true;
    if (normA.includes("market") && normB.includes("market")) return true;
    return false;
  };

  const marketerCategoryTab = connectedProfiles.find((tab) => {
    const tId = String(tab.connectedProfileTabId || tab.id || "")
      .replace(/^type:/, "")
      .toLowerCase()
      .replace(/[\s_-]+/g, "");
    return tId === "marketershop" || isMatchingCategory(tId, "marketerShop");
  });
  const marketerCategoryUnread =
    Number(marketerCategoryTab?.unreadCount) || 0;

  const visibleConnectedProfiles = showProfileSwitcherTabs
    ? connectedProfiles.filter(
        (profile) => profile.connectedProfileTabId !== "type:user",
      )
    : connectedProfiles;

  const unifiedCategoryTabs = useMemo(() => {
    const tabsMap = new Map();

    const normalizeType = (key) =>
      String(key || "")
        .replace(/^type:/, "")
        .toLowerCase()
        .replace(/[\s_-]+/g, "");

    // 1. Add from profileTabs (user's owned shops)
    if (showProfileSwitcherTabs && Array.isArray(profileTabs)) {
      profileTabs.forEach((tab) => {
        const typeKey = tab.typeKey;
        const normKey = normalizeType(typeKey);
        if (!normKey || normKey === "user" || normKey === "generalpublic") return;

        tabsMap.set(normKey, {
          typeKey,
          tabId: `type:${typeKey}`,
          label: tab.label,
          primaryShop: tab.primaryShop,
          shops: tab.shops || [],
          connectedProfile: null,
          connectedProfiles: [],
          unreadCount: (tab.shops || []).reduce(
            (sum, s) => sum + (Number(s.unreadCount) || 0),
            0,
          ),
        });
      });
    }

    // 2. Merge or add from visibleConnectedProfiles (connected profile categories)
    if (Array.isArray(visibleConnectedProfiles)) {
      visibleConnectedProfiles.forEach((profile) => {
        const profileTabId = String(profile.connectedProfileTabId || "");
        const normKey = normalizeType(profileTabId);
        if (!normKey || normKey === "user" || normKey === "generalpublic") return;

        const profileLabel =
          profile.tabLabel || profile.name || "Connected profile";
        const profileUnread = Number(profile.unreadCount) || 0;
        const connectedList = Array.isArray(profile.connectedProfiles)
          ? profile.connectedProfiles
          : [];

        // Check exact match or category match
        let existingKey = tabsMap.has(normKey) ? normKey : null;
        if (!existingKey) {
          for (const key of tabsMap.keys()) {
            if (isMatchingCategory(key, normKey)) {
              existingKey = key;
              break;
            }
          }
        }

        if (existingKey) {
          // Merge with existing owned shop tab to avoid duplicates and show count!
          const existing = tabsMap.get(existingKey);
          existing.connectedProfile = profile;
          existing.connectedProfiles = connectedList;
          existing.unreadCount += profileUnread;
        } else {
          // New category tab from connections!
          tabsMap.set(normKey, {
            typeKey: profileTabId.replace(/^type:/, ""),
            tabId: profileTabId,
            label: profileLabel,
            primaryShop: null,
            shops: [],
            connectedProfile: profile,
            connectedProfiles: connectedList,
            unreadCount: profileUnread,
          });
        }
      });
    }

    return Array.from(tabsMap.values());
  }, [profileTabs, visibleConnectedProfiles, showProfileSwitcherTabs]);

  const updateScrollButtons = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 2);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 2);
  }, []);

  useEffect(() => {
    updateScrollButtons();
    const handleResize = () => updateScrollButtons();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [updateScrollButtons, unifiedCategoryTabs.length]);

  // Mouse wheel horizontal translation
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const handleWheel = (e) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        track.scrollLeft += e.deltaY;
        updateScrollButtons();
      }
    };

    track.addEventListener("wheel", handleWheel, { passive: false });
    return () => track.removeEventListener("wheel", handleWheel);
  }, [updateScrollButtons]);

  // Auto-scroll active tab into view
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const activeBtn = track.querySelector(`.${styles.profileTabActive}`);
    if (activeBtn) {
      activeBtn.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
    const timer = setTimeout(updateScrollButtons, 300);
    return () => clearTimeout(timer);
  }, [
    selectedConnectedTabId,
    isUserActiveTab,
    isMarketerActiveTab,
    updateScrollButtons,
  ]);

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    isMouseDownRef.current = true;
    isDraggingRef.current = false;
    startXRef.current = e.pageX - (trackRef.current?.offsetLeft || 0);
    scrollLeftRef.current = trackRef.current?.scrollLeft || 0;
  };

  const handleMouseMove = (e) => {
    if (!isMouseDownRef.current || !trackRef.current) return;
    const x = e.pageX - (trackRef.current?.offsetLeft || 0);
    const walk = (x - startXRef.current) * 1.3;
    if (Math.abs(walk) > 4) {
      isDraggingRef.current = true;
    }
    trackRef.current.scrollLeft = scrollLeftRef.current - walk;
    updateScrollButtons();
  };

  const handleMouseUp = () => {
    isMouseDownRef.current = false;
    setTimeout(() => {
      isDraggingRef.current = false;
    }, 60);
  };

  const handleTabClick = (callback) => (e) => {
    if (isDraggingRef.current) {
      e.preventDefault();
      return;
    }
    callback(e);
  };

  const handleScrollLeft = () => {
    if (trackRef.current) {
      trackRef.current.scrollBy({ left: -160, behavior: "smooth" });
      setTimeout(updateScrollButtons, 260);
    }
  };

  const handleScrollRight = () => {
    if (trackRef.current) {
      trackRef.current.scrollBy({ left: 160, behavior: "smooth" });
      setTimeout(updateScrollButtons, 260);
    }
  };

  const handleTouchStart = (e) => {
    if (!e.touches || e.touches.length === 0) return;
    startXRef.current = e.touches[0].pageX - (trackRef.current?.offsetLeft || 0);
    scrollLeftRef.current = trackRef.current?.scrollLeft || 0;
    isDraggingRef.current = false;
  };

  const handleTouchMove = (e) => {
    if (!e.touches || e.touches.length === 0 || !trackRef.current) return;
    const x = e.touches[0].pageX - (trackRef.current?.offsetLeft || 0);
    const walk = (x - startXRef.current) * 1.3;
    if (Math.abs(walk) > 4) {
      isDraggingRef.current = true;
    }
  };

  if (isLoading || (isError && profiles.length === 0)) {
    return (
      <div className={styles.profileTabsSliderWrapper}>
        <div
          className={`${styles.profileTabs} ${styles.profileTabsLoading}`}
          role={isError ? "alert" : "status"}
          aria-label={isError ? "Unable to load profiles" : "Loading profiles"}
        >
          {(focusProfile ? [120] : [90, 110, 100, 115, 95]).map(
            (width, index) => (
              <span
                key={index}
                className={styles.profileTabSkeleton}
                style={{ width, height: 27, borderRadius: 6 }}
                aria-hidden="true"
              />
            ),
          )}
          {isError && (
            <button
              type="button"
              className={styles.profileRetryButton}
              onClick={onRetry}
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.profileTabsSliderWrapper}>
      <div
        ref={trackRef}
        className={styles.profileTabs}
        onScroll={updateScrollButtons}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleMouseUp}
      >
        {showProfileSwitcherTabs && (
          <button
            key="user-profile-tab"
            type="button"
            className={`${styles.profileTab} ${
              isUserActiveTab ? styles.profileTabActive : ""
            }`}
            aria-label={`User Profile${userUnreadCount > 0 ? `, ${userUnreadCount} unread messages` : ""}`}
            onClick={handleTabClick(() => {
              if (onSelectUserProfile) {
                onSelectUserProfile(userProfile);
              } else if (userProfile) {
                handleSelect(userProfile);
              }
            })}
          >
            User Profile
            {userUnreadCount > 0 && (
              <span className={styles.profileUnreadDot} aria-hidden="true" />
            )}
          </button>
        )}

        {unifiedCategoryTabs.length > 0 && (
          <div
            className={styles.focusedConnections}
            aria-label="Category profiles"
          >
            {unifiedCategoryTabs.map((tab) => {
              const normTypeKey = String(tab.typeKey || "")
                .toLowerCase()
                .replace(/[\s_-]+/g, "");
              const normSelected = String(selectedConnectedTabId || "")
                .replace(/^type:/, "")
                .toLowerCase()
                .replace(/[\s_-]+/g, "");

              const selectedProfileNorm =
                selectedProfile &&
                !selectedProfile.isGeneralPublic &&
                !selectedProfile.isMarketer
                  ? String(getMessengerProfileTypeKey(selectedProfile) || "")
                      .toLowerCase()
                      .replace(/[\s_-]+/g, "")
                  : "";

              const isTabActive = Boolean(
                !forceGeneralPublicActive &&
                !isUserActiveTab &&
                !isMarketerActiveTab &&
                (normSelected === normTypeKey ||
                  selectedConnectedTabId === tab.tabId ||
                  (!normSelected && selectedProfileNorm === normTypeKey))
              );

              const tabUnread =
                (Number(tab.unreadCount) || 0) +
                (isTabActive ? activeChatsUnreadCount : 0);

              return (
                <button
                  key={`tab-${tab.tabId}`}
                  type="button"
                  className={`${styles.profileTab} ${
                    isTabActive ? styles.profileTabActive : ""
                  }`}
                  aria-pressed={isTabActive}
                  aria-label={`${tab.label}${tabUnread > 0 ? `, ${tabUnread} unread messages` : ""}`}
                  onClick={handleTabClick(() => {
                    if (onOpenConnectedProfile) {
                      onOpenConnectedProfile(
                        tab.connectedProfile || {
                          connectedProfileTabId: tab.tabId,
                          tabId: tab.tabId,
                        },
                      );
                    }
                    if (tab.primaryShop && onSelectShopProfile) {
                      onSelectShopProfile(tab.primaryShop);
                    } else if (tab.primaryShop) {
                      handleSelect(tab.primaryShop);
                    }
                  })}
                >
                  {tab.label}
                  {tabUnread > 0 && (
                    <span
                      className={styles.profileUnreadDot}
                      aria-hidden="true"
                    />
                  )}
                </button>
              );
            })}
          </div>
        )}

        {!showProfileSwitcherTabs && unifiedCategoryTabs.length === 0 && (
          <div
            className={styles.focusedConnections}
            aria-label={
              isUserActive
                ? "General Public connected profiles"
                : "Shop connected profiles"
            }
          >
            {connectedProfilesLoading ? (
              <span className={styles.connectedProfileTabStatus} role="status">
                Loading connected profiles…
              </span>
            ) : connectedProfilesError ? (
              <span className={styles.connectedProfileTabStatus} role="alert">
                {connectedProfilesError}
              </span>
            ) : null}
          </div>
        )}

        {showProfileSwitcherTabs &&
          marketerProfile &&
          (() => {
            const marketerUnreadCount = Math.max(
              (Number(marketerProfile.unreadCount) || 0) + marketerCategoryUnread,
              isMarketerActive ? activeChatsUnreadCount : 0,
            );

            return (
              <button
                key="marketing-profile-tab"
                type="button"
                className={`${styles.profileTab} ${
                  isMarketerActiveTab ? styles.profileTabActive : ""
                }`}
                aria-label={`Marketing Profile${marketerUnreadCount > 0 ? `, ${marketerUnreadCount} unread messages` : ""}`}
                onClick={handleTabClick(() => {
                  if (onSelectMarketerProfile) {
                    onSelectMarketerProfile(marketerProfile);
                  } else {
                    handleSelect(marketerProfile);
                  }
                })}
              >
                Marketing Profile
                {marketerUnreadCount > 0 && (
                  <span className={styles.profileUnreadDot} aria-hidden="true" />
                )}
              </button>
            );
          })()}
      </div>
    </div>
  );
};

export { MessengerProfileTabs };
export default MessengerProfileTabs;
